import {
    Checkbox,
    GuaranteeCalculation,
    INoShowProtection,
    PaymentMethodType,
    PaymentUtils,
    Segmented,
    Switch,
    TextField,
} from '@localina/core';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// No-Show Protection, part 2: per shift, guests leave a payment guarantee (card / TWINT) that the restaurant may
// charge if they don't show up. Sits under "Vorlaufzeit" in the shift drawer, same look as the request-mode block.

const CURRENCY = 'chf';
const DEFAULT_PROTECTION: INoShowProtection = {
    amount: 3000,
    calculation: 'PER_PERSON',
    minPersons: null,
    paymentMethods: ['CARD'],
    depositDeadlineHours: 24,
    googleExclusionAcknowledged: false,
};

interface IProps {
    value?: INoShowProtection | null;
    onChange: (value: INoShowProtection | null) => void;
    /** the restaurant's payment account can take payments; otherwise the block stays disabled */
    accountReady: boolean;
    /** methods enabled on the payment account (TWINT only after the provider approved it) */
    availableMethods: PaymentMethodType[];
    /** the shift runs in request mode: a shift works either with requests or with guarantees, never both */
    requestModeActive: boolean;
    /** restaurant-wide free cancellation (Manage restaurants), in hours */
    cancelNoticeHours: number;
}

/** A guarantee is complete when it has an amount, a method and the Google acknowledgement. */
export const isNoShowProtectionValid = (protection?: INoShowProtection | null) =>
    !protection ||
    (protection.amount > 0 &&
        protection.paymentMethods.length > 0 &&
        !!protection.googleExclusionAcknowledged &&
        (protection.minPersons === null || protection.minPersons === undefined || protection.minPersons >= 1) &&
        (protection.depositDeadlineHours === null ||
            protection.depositDeadlineHours === undefined ||
            protection.depositDeadlineHours >= 1));

export const NoShowProtectionBlock: React.FC<IProps> = (props) => {
    const { t, i18n } = useTranslation();
    const protection = props.value ?? null;
    const enabled = !!protection;

    // the amount is typed in francs ("30" / "12.50") and stored in Rappen
    const [amountText, setAmountText] = useState('');
    // follows the value when it changes from outside (another shift opened), not while it's being typed
    useEffect(() => {
        if (!protection) {
            setAmountText('');
            return;
        }
        const typed = PaymentUtils.parseAmount(amountText);
        const typedAmount = typed !== null ? PaymentUtils.toMinorUnits(typed, CURRENCY) : 0;
        if (typedAmount !== protection.amount) {
            setAmountText(PaymentUtils.toMajorUnits(protection.amount, CURRENCY));
        }
    }, [enabled, protection?.amount]);

    const update = (changes: Partial<INoShowProtection>) => {
        if (protection) {
            props.onChange({ ...protection, ...changes });
        }
    };

    const toggle = (on: boolean) =>
        props.onChange(
            on
                ? {
                      ...DEFAULT_PROTECTION,
                      paymentMethods: DEFAULT_PROTECTION.paymentMethods.filter((method) =>
                          props.availableMethods.includes(method),
                      ),
                  }
                : null,
        );

    const handleAmount = (text: string) => {
        setAmountText(text);
        // "1'000", "1.000", "30,50" ...; anything else counts as no amount and blocks saving
        const value = PaymentUtils.parseAmount(text);
        update({ amount: value && value > 0 ? PaymentUtils.toMinorUnits(value, CURRENCY) : 0 });
    };

    const toOptionalNumber = (text: string) => {
        const value = parseInt(text, 10);
        return Number.isFinite(value) && value > 0 ? value : null;
    };

    const previewPersons = Math.max(protection?.minPersons ?? 2, 1);
    const previewTotal = protection
        ? protection.calculation === 'PER_PERSON'
            ? protection.amount * previewPersons
            : protection.amount
        : 0;
    const format = (amount: number) => PaymentUtils.formatAmount(amount, CURRENCY, i18n.language);

    return (
        <div className="no-show-protection">
            <Switch
                label={t('availability.shiftView.noShowProtection.title')}
                description={t(
                    !props.accountReady
                        ? 'availability.shiftView.noShowProtection.connectFirst'
                        : props.requestModeActive && !enabled
                        ? 'availability.shiftView.noShowProtection.requestModeInfo'
                        : 'availability.shiftView.noShowProtection.info',
                )}
                checked={enabled}
                disabled={(!props.accountReady || props.requestModeActive) && !enabled}
                onChange={toggle}
            />
            {protection && (
                <div className="no-show-protection__panel">
                    {props.requestModeActive && (
                        // stored before the rule existed: saving is blocked until one of the two is switched off
                        <div className="no-show-protection__note warn">
                            {t('availability.shiftView.noShowProtection.requestModeInfo')}
                        </div>
                    )}
                    <span className="no-show-protection__label">
                        {t('availability.shiftView.noShowProtection.type')}
                    </span>
                    <p className="no-show-protection__hint">{t('availability.shiftView.noShowProtection.typeInfo')}</p>

                    <div className="no-show-protection__row">
                        <TextField
                            name="noShowAmount"
                            label={t('availability.shiftView.noShowProtection.amount')}
                            value={amountText}
                            onChange={handleAmount}
                            required
                            hasError={protection.amount <= 0}
                        />
                        <Segmented
                            value={protection.calculation}
                            onChange={(calculation: GuaranteeCalculation) => update({ calculation })}
                            options={[
                                {
                                    value: 'PER_PERSON',
                                    label: t('availability.shiftView.noShowProtection.perPerson'),
                                },
                                {
                                    value: 'PER_RESERVATION',
                                    label: t('availability.shiftView.noShowProtection.perReservation'),
                                },
                            ]}
                        />
                    </div>

                    <TextField
                        name="noShowMinPersons"
                        type="number"
                        label={t('availability.shiftView.noShowProtection.minPersons')}
                        value={protection.minPersons ? `${protection.minPersons}` : ''}
                        helperText={t('availability.shiftView.noShowProtection.minPersonsInfo')}
                        onChange={(text) => update({ minPersons: toOptionalNumber(text) })}
                    />

                    <p className="no-show-protection__hint">
                        {t('availability.shiftView.noShowProtection.freeCancellation', {
                            hours: props.cancelNoticeHours,
                        })}
                    </p>

                    <span className="no-show-protection__label">
                        {t('availability.shiftView.noShowProtection.methods')}
                    </span>
                    <Segmented
                        fullWidth
                        value={protection.paymentMethods}
                        onChange={(paymentMethods: PaymentMethodType[]) => update({ paymentMethods })}
                        options={(['CARD', 'TWINT'] as PaymentMethodType[]).map((method) => ({
                            value: method,
                            label: t(`integrations.items.payments.methods.${method}`),
                            disabled:
                                !props.availableMethods.includes(method) && !protection.paymentMethods.includes(method),
                        }))}
                    />
                    <p className="no-show-protection__hint">
                        {t(
                            props.availableMethods.includes('TWINT')
                                ? 'availability.shiftView.noShowProtection.twintInfo'
                                : 'availability.shiftView.noShowProtection.twintPending',
                        )}
                    </p>

                    <TextField
                        name="noShowDepositDeadline"
                        type="number"
                        label={t('availability.shiftView.noShowProtection.deadline')}
                        value={protection.depositDeadlineHours ? `${protection.depositDeadlineHours}` : ''}
                        helperText={t('availability.shiftView.noShowProtection.deadlineInfo')}
                        onChange={(text) => update({ depositDeadlineHours: toOptionalNumber(text) })}
                    />

                    <span className="no-show-protection__label">
                        {t('availability.shiftView.noShowProtection.preview')}
                    </span>
                    <div className="no-show-protection__preview">
                        <b>
                            {t('availability.shiftView.noShowProtection.previewAmount', {
                                amount: format(previewTotal),
                                persons: previewPersons,
                            })}
                        </b>{' '}
                        {t('availability.shiftView.noShowProtection.previewText', {
                            hours: props.cancelNoticeHours,
                        })}
                    </div>

                    <div className="no-show-protection__google">
                        <div className="no-show-protection__note warn">
                            <b>{t('availability.shiftView.noShowProtection.googleTitle')}</b>{' '}
                            {t('availability.shiftView.noShowProtection.googleInfo')}
                        </div>
                        <Checkbox
                            label={t('availability.shiftView.noShowProtection.googleAcknowledge')}
                            checked={!!protection.googleExclusionAcknowledged}
                            onChange={(googleExclusionAcknowledged) => update({ googleExclusionAcknowledged })}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
